/**
 * Hooks granulares de "puedo o no puedo hacer X" combinando:
 *   - canMutate (rol + suscripción OPUS 27)
 *   - cuota mensual del counter relevante (proyectos / chats / conceptos)
 *
 * Cada hook devuelve `{ allowed, reason }` para que la UI pueda:
 *   - deshabilitar el botón (`!allowed`)
 *   - mostrar tooltip explicativo con el `reason`
 *
 * Razones posibles (en orden de prioridad):
 *   1. 'viewer' — rol viewer no muta.
 *   2. 'expired' — suscripción OPUS 27 vencida.
 *   3. 'quota' — cuota mensual del counter alcanzada o ilimitada.
 *
 * Si todo OK: `{ allowed: true }` sin reason.
 */
import { useAuthStore } from './authStore.js';
import { useUsageStore } from './usageStore.js';

export type GuardReason =
  | 'viewer'
  | 'expired'
  | 'quota_projects'
  | 'quota_chats'
  | 'quota_concepts';

export interface Guard {
  allowed: boolean;
  reason?: GuardReason;
  /** Mensaje human-readable apto para tooltip. */
  message?: string;
}

function baseGuard(): Guard | null {
  const auth = useAuthStore.getState();
  // Subscription vencida tiene prioridad sobre rol — más claro para el user.
  if (auth.subscriptionExpired) {
    return {
      allowed: false,
      reason: 'expired',
      message: 'Tu suscripción OPUS 27 está vencida. Renuévala para crear o modificar.',
    };
  }
  const role = auth.tenant?.role;
  if (role === 'viewer') {
    return {
      allowed: false,
      reason: 'viewer',
      message: 'Tu rol es Viewer — sólo lectura.',
    };
  }
  if (role !== 'owner' && role !== 'admin' && role !== 'member') {
    return {
      allowed: false,
      reason: 'viewer',
      message: 'Tu rol no permite esta acción.',
    };
  }
  return null;
}

function quotaGuard(
  counter: 'projects_created' | 'chats_sent' | 'concepts_quoted',
  quotaReason: GuardReason,
  msgWhenFull: string,
): Guard | null {
  const usage = useUsageStore.getState().data;
  if (!usage) return null; // todavía no cargado — no bloquear
  const current = usage.counters[counter];
  const limit = usage.limits[counter];
  if (limit !== null && current >= limit) {
    return {
      allowed: false,
      reason: quotaReason,
      message: msgWhenFull.replace('{limit}', String(limit)).replace('{current}', String(current)),
    };
  }
  return null;
}

/**
 * Hook reactivo: devuelve `Guard` que cambia cuando authStore o usageStore
 * cambian. Internamente subscribe a ambos.
 */
function useReactiveGuard(
  counter: 'projects_created' | 'chats_sent' | 'concepts_quoted' | null,
  quotaReason: GuardReason,
  quotaMsg: string,
): Guard {
  // Subscribe a los campos que importan para que el componente se re-renderice.
  const subscriptionExpired = useAuthStore((s) => s.subscriptionExpired);
  const role = useAuthStore((s) => s.tenant?.role);
  const usage = useUsageStore((s) => s.data);

  // Volver a evaluar igual que las helpers de arriba, pero leyendo del store
  // suscrito (no del .getState() puntual). Necesario para que React detecte cambios.
  if (subscriptionExpired) {
    return {
      allowed: false,
      reason: 'expired',
      message: 'Tu suscripción OPUS 27 está vencida. Renuévala para crear o modificar.',
    };
  }
  if (role === 'viewer') {
    return { allowed: false, reason: 'viewer', message: 'Tu rol es Viewer — sólo lectura.' };
  }
  if (role !== 'owner' && role !== 'admin' && role !== 'member') {
    return { allowed: false, reason: 'viewer', message: 'Tu rol no permite esta acción.' };
  }
  if (counter && usage) {
    const current = usage.counters[counter];
    const limit = usage.limits[counter];
    if (limit !== null && current >= limit) {
      return {
        allowed: false,
        reason: quotaReason,
        message: quotaMsg.replace('{limit}', String(limit)).replace('{current}', String(current)),
      };
    }
  }
  return { allowed: true };
}

/** ¿Puede crear un nuevo proyecto? */
export function useCanCreateProject(): Guard {
  return useReactiveGuard(
    'projects_created',
    'quota_projects',
    'Cuota mensual de proyectos alcanzada ({current} / {limit}). Renueva el ciclo o sube de plan.',
  );
}

/** ¿Puede enviar un mensaje de chat? */
export function useCanSendChat(): Guard {
  return useReactiveGuard(
    'chats_sent',
    'quota_chats',
    'Cuota mensual de chats alcanzada ({current} / {limit}). Renueva el ciclo o sube de plan.',
  );
}

/** ¿Puede correr una nueva cotización (concepts_quoted)?
 *  Nota: el check exacto es por items.length de la cotización pendiente vs
 *  remaining, lo cual sólo conoce el backend. Acá bloqueamos si ya llegó
 *  al tope para evitar que el usuario empiece algo que va a fallar. */
export function useCanQuote(): Guard {
  return useReactiveGuard(
    'concepts_quoted',
    'quota_concepts',
    'Cuota mensual de conceptos alcanzada ({current} / {limit}). Renueva el ciclo o sube de plan.',
  );
}

/** Para acciones de mutación sin counter (ej. eliminar, editar nombre). */
export function useCanMutateGeneric(): Guard {
  return useReactiveGuard(null, 'quota_projects', '');
}

// Re-exports para uso fuera de React (no recomendado pero a veces útil).
export { baseGuard, quotaGuard };
